import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from './UserContext';
import { getApiUrl } from '@/lib/api-config';

/**
 * What the signed-in person may do in the company (GET /api/permissions/me): the app hides the navigation and the screens of a resource whose View the role does
 * not have. This is a convenience, NOT the boundary: the server refuses the same requests whatever the screen shows (and the database repeats it with RLS).
 *
 * While the answer is not known yet, or if it cannot be read, nothing is hidden (`can` says yes): a server that cannot answer must not blank the whole app, and the
 * server's own refusals still apply.
 */
export const PERMISSIONS_CHANGED_EVENT = 'krewup:permissions-changed';

type PermissionsValue = {
  /** true once the list has been read */
  ready: boolean;
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  refresh: () => Promise<void>;
};

const defaultValue: PermissionsValue = { ready: false, can: () => true, canAny: () => true, refresh: async () => undefined };
const PermissionsContext = createContext<PermissionsValue>(defaultValue);

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { companyId, userRole } = useUser();
  const [granted, setGranted] = useState<Set<string> | null>(null);

  const refresh = useCallback(async () => {
    if (!companyId) { setGranted(null); return; }
    try {
      const response = await fetch(getApiUrl('api/permissions/me'));
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success || !Array.isArray(result.data?.permissions)) throw new Error('permissions unavailable');
      setGranted(new Set<string>(result.data.permissions));
    } catch (error) {
      console.error('Could not read the permissions of the current user; nothing is hidden:', error);
      setGranted(null);
    }
  }, [companyId]);

  // re-read when the company or the role changes, and when the admin saves the matrix (their own navigation may change)
  useEffect(() => { void refresh(); }, [refresh, userRole]);
  useEffect(() => {
    const onChanged = () => { void refresh(); };
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PERMISSIONS_CHANGED_EVENT, onChanged);
  }, [refresh]);

  const value = useMemo<PermissionsValue>(() => ({
    ready: granted !== null,
    can: (permission) => (granted === null ? true : granted.has(permission)),
    canAny: (permissions) => (granted === null ? true : permissions.some((p) => granted.has(p))),
    refresh,
  }), [granted, refresh]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export const usePermissions = () => useContext(PermissionsContext);
